import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { useAuthContext } from '@emfood/shared';

import CancelIcon from '@mui/icons-material/Cancel';
import {
  Stack,
  Dialog,
  Button,
  Checkbox,
  TextField,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Typography,
} from '@mui/material';

import { useControlFavouritesAccess } from 'src/api/hooks/api-hooks';

interface ControlAccessDialogProps {
  open: boolean;
  onClose: () => void;
  collectionId: string;
  currentBuyerSites?: Array<{ id: string; name: string }>;
  buyerSites: Array<{ id: string; name: string }>;
  isSharedWithOrg?: boolean;
}

export default function ControlAccessDialog({
  open,
  onClose,
  collectionId,
  currentBuyerSites = [],
  buyerSites,
  isSharedWithOrg = false,
}: ControlAccessDialogProps) {
  const [selectedSites, setSelectedSites] =
    useState<Array<{ id: string; name: string }>>(currentBuyerSites);
  const [assignToOrg, setAssignToOrg] = useState(isSharedWithOrg);

  const { user } = useAuthContext();
  const { mutate: controlAccess } = useControlFavouritesAccess();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleSave = () => {
    controlAccess(
      {
        pathParams: { collectionId },
        body: assignToOrg
          ? { buyerOrgId: user?.buyerOrg?.id }
          : { buyerSiteIds: selectedSites.map((site) => site.id) },
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Access control updated successfully!');
          queryClient.invalidateQueries('favourites');
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h2">Control Access</Typography>
        <Typography variant="subtitle2">Assign sites or share with entire organisation</Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Autocomplete
            multiple
            options={buyerSites}
            disablePortal
            getOptionLabel={(option) => option.name}
            value={selectedSites}
            onChange={(_, newValue) => setSelectedSites(newValue)}
            disabled={assignToOrg}
            renderInput={(params) => (
              <TextField {...params} label="Buyer Sites" placeholder="Select sites" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            ChipProps={{
              deleteIcon: <CancelIcon />,
            }}
          />
          <FormControlLabel
            control={
              <Checkbox checked={assignToOrg} onChange={(e) => setAssignToOrg(e.target.checked)} />
            }
            label="Assign to entire organisation"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
